import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Enable httpOnly cookie authentication
});

// One Idempotency-Key per payment attempt: double taps and retries reuse it, so money moves once
const MONEY_PATHS = /^\/?(transfer\/send(-by-phone|-to-bank)?|exchange\/convert|payment-requests\/[^/]+\/pay|batch\/send|merchants\/pay\/[^/]+)$/;
const pendingKeys = new Map();
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

api.interceptors.request.use((config) => {
  if (config.method === 'post' && MONEY_PATHS.test((config.url || '').split('?')[0])) {
    const fingerprint = `${config.url}|${typeof config.data === 'string' ? config.data : JSON.stringify(config.data || {})}`;
    if (!pendingKeys.has(fingerprint)) pendingKeys.set(fingerprint, newKey());
    config.headers['Idempotency-Key'] = pendingKeys.get(fingerprint);
    config.idempotencyFingerprint = fingerprint;
  }
  return config;
});

const settleKey = (config, hasResponse) => {
  if (config?.idempotencyFingerprint && hasResponse) pendingKeys.delete(config.idempotencyFingerprint);
};

api.interceptors.response.use(
  (response) => { settleKey(response.config, true); return response; },
  (error) => { settleKey(error.config, Boolean(error.response) && error.response.status !== 409); return Promise.reject(error); }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Try to refresh token first
      try {
        await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });
        // Retry original request
        return api.request(error.config);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Wallet APIs
export const getWalletBalances = () => api.get('/wallet/balances');
export const addCurrency = (currency) => api.post(`/wallet/add-currency?currency=${currency}`);
export const removeCurrency = (currency) => api.delete(`/wallet/remove-currency/${currency}`);

// Deposit APIs
export const createDepositSession = (amount, currency, originUrl) => 
  api.post('/deposit/create-session', { amount, currency, origin_url: originUrl });
export const getDepositStatus = (sessionId) => api.get(`/deposit/status/${sessionId}`);

// Payment Methods (enabled/disabled flags)
export const getPaymentMethods = () => api.get('/payments/methods');
export const updatePaymentMethods = (data) => api.put('/payments/methods', data);

// PayPal APIs
export const getPayPalConfig = () => api.get('/paypal/config');
export const createPayPalOrder = (amount, currency) => api.post('/paypal/create-order', { amount, currency });
export const capturePayPalOrder = (orderId) => api.post(`/paypal/orders/${orderId}/capture`);

// Transfer APIs
export const sendFunds = (recipientEmail, amount, currency, note, recipientTag = null) =>
  api.post('/transfer/send', { 
    recipient_email: recipientEmail, 
    recipient_tag: recipientTag,
    amount, 
    currency, 
    note 
  });

// Transaction APIs
export const getTransactions = (limit = 50, offset = 0) => 
  api.get(`/transactions?limit=${limit}&offset=${offset}`);
export const getTransactionStats = () => api.get('/transactions/stats');

// Receipt Email Settings
export const getReceiptEmailSetting = () => api.get('/auth/settings/receipt-emails');
export const updateReceiptEmailSetting = (enabled) => api.patch('/auth/settings/receipt-emails', { enabled });

// WhatsApp Preferences
export const getWhatsAppPreferences = () => api.get('/users/me/whatsapp-preferences');
export const updateWhatsAppPreferences = (data) => api.put('/users/me/whatsapp-preferences', data);

// WhatsApp Admin
export const getWhatsAppAdminSettings = () => api.get('/admin/whatsapp/settings');
export const updateWhatsAppAdminSettings = (enabled) => api.put('/admin/whatsapp/settings', { enabled });
export const getWhatsAppConfigStatus = () => api.get('/admin/whatsapp/config-status');
export const getWhatsAppLogs = (limit = 50) => api.get(`/admin/whatsapp/logs?limit=${limit}`);
export const sendWhatsAppTestMessage = () => api.post('/admin/whatsapp/test-message');
export const getWhatsAppInbox = (search) => api.get('/admin/whatsapp/inbox', { params: search ? { search } : {} });
export const getWhatsAppThread = (waId) => api.get(`/admin/whatsapp/inbox/${waId}/messages`);
export const markWhatsAppThreadRead = (waId) => api.post(`/admin/whatsapp/inbox/${waId}/read`);
export const replyWhatsAppThread = (waId, text) => api.post(`/admin/whatsapp/inbox/${waId}/reply`, { text });
export const getWhatsAppWebhookStatus = () => api.get('/admin/whatsapp/webhook-status');
export const getWhatsAppUnreadCount = () => api.get('/admin/whatsapp/inbox/unread-count');
export const sendWhatsAppFollowUp = (waId, reference) => api.post(`/admin/whatsapp/inbox/${waId}/follow-up`, { reference });

// Low Balance Alerts
export const getBalanceAlerts = () => api.get('/alerts/balance');
export const updateBalanceAlerts = (alerts) => api.put('/alerts/balance', { alerts });
export const deleteBalanceAlert = (currency) => api.delete(`/alerts/balance/${currency}`);

// Invoice APIs (Personal)
export const createPersonalInvoice = (data) => api.post('/invoices/create', data);
export const listPersonalInvoices = (limit = 20, offset = 0) => api.get(`/invoices/list?limit=${limit}&offset=${offset}`);
export const getPersonalInvoice = (invoiceId) => api.get(`/invoices/${invoiceId}`);
export const getPersonalInvoicePdfUrl = (invoiceId) => `${API_URL}/invoices/${invoiceId}/pdf`;
export const getBusinessDetails = () => api.get('/invoices/business-details');
export const updateBusinessDetails = (data) => api.put('/invoices/business-details', data);

// Invoice Payment Links APIs
export const generateInvoicePaymentLink = (invoiceId) => api.post(`/invoices/${invoiceId}/payment-link`);
export const getInvoicePaymentLink = (invoiceId) => api.get(`/invoices/${invoiceId}/payment-link`);
export const getInvoiceByPaymentToken = (paymentToken) => api.get(`/invoices/pay/${paymentToken}`);
export const payInvoiceByToken = (paymentToken, data) => api.post(`/invoices/pay/${paymentToken}/complete`, data);

// Export APIs
export const getExportSummary = (startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/export/transactions/summary?${params.toString()}`);
};

export const downloadTransactionsCSV = (startDate, endDate, type, currency) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (type && type !== 'all') params.append('transaction_type', type);
  if (currency && currency !== 'all') params.append('currency', currency);
  return `${API_URL}/export/transactions/csv?${params.toString()}`;
};

export const downloadTransactionsPDF = (startDate, endDate, type, currency) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (type && type !== 'all') params.append('transaction_type', type);
  if (currency && currency !== 'all') params.append('currency', currency);
  return `${API_URL}/export/transactions/pdf?${params.toString()}`;
};

// Exchange APIs
export const getExchangeRates = (base = 'USD') => api.get(`/exchange/rates?base=${base}`);
export const getCurrencies = () => api.get('/exchange/currencies');
export const convertCurrency = (fromCurrency, toCurrency, amount) =>
  api.post('/exchange/convert', { from_currency: fromCurrency, to_currency: toCurrency, amount });

// Favorite Currency Pairs APIs
export const getFavoritePairs = () => api.get('/exchange/favorites');
export const addFavoritePair = (fromCurrency, toCurrency) =>
  api.post(`/exchange/favorites?from_currency=${fromCurrency}&to_currency=${toCurrency}`);
export const removeFavoritePair = (favoriteId) =>
  api.delete(`/exchange/favorites/${favoriteId}`);
export const removeFavoriteByPair = (fromCurrency, toCurrency) =>
  api.delete(`/exchange/favorites/pair/${fromCurrency}/${toCurrency}`);

// Card APIs
export const createVirtualCard = (cardType = 'standard') => 
  api.post('/cards/create', { card_type: cardType });
export const getVirtualCards = () => api.get('/cards');
export const updateCardStatus = (cardId, status) => 
  api.patch(`/cards/${cardId}/status?status=${status}`);
export const deleteCard = (cardId) => api.delete(`/cards/${cardId}`);

// Card Spending & Analytics APIs
export const recordCardSpending = (cardId, data) =>
  api.post(`/cards/${cardId}/spend`, data);
export const simulateCardSpending = (cardId, count = 5) =>
  api.post(`/cards/${cardId}/simulate-spending?count=${count}`);
export const getCardTransactions = (cardId, limit = 50) =>
  api.get(`/cards/${cardId}/transactions?limit=${limit}`);
export const getCardAnalytics = (cardId) =>
  api.get(`/cards/${cardId}/analytics`);
export const getAllCardsAnalytics = () =>
  api.get('/cards/analytics/overview');

// Card Budgets & Alerts APIs
export const setCardBudgets = (cardId, budgets) =>
  api.post(`/cards/${cardId}/budgets`, budgets);
export const getCardBudgets = (cardId) =>
  api.get(`/cards/${cardId}/budgets`);
export const getCardAlerts = (cardId) =>
  api.get(`/cards/${cardId}/alerts`);
export const getAllCardsAlerts = () =>
  api.get('/cards/alerts/all');

// Export APIs
export const exportCardCSV = (cardId) =>
  api.get(`/cards/${cardId}/export/csv`, { responseType: 'blob' });
export const exportCardReport = (cardId) =>
  api.get(`/cards/${cardId}/export/report`);
export const exportAllCardsCSV = () =>
  api.get('/cards/export/all-csv', { responseType: 'blob' });

// Email Notifications APIs
export const updateNotificationPreferences = (prefs) =>
  api.post('/notifications/preferences', prefs);
export const getNotificationPreferences = () =>
  api.get('/notifications/preferences');
export const getNotificationHistory = (limit = 20) =>
  api.get(`/notifications/history?limit=${limit}`);
export const sendSpendingAlert = (cardId) =>
  api.post(`/notifications/send-alert?card_id=${cardId}`);
export const getSpendingStatus = () =>
  api.get('/notifications/spending-status');

// Scheduled Reports APIs
export const getReportPreferences = () =>
  api.get('/notifications/reports/preferences');
export const updateReportPreferences = (prefs) =>
  api.post('/notifications/reports/preferences', prefs);
export const sendReportNow = (reportType = 'weekly') =>
  api.post(`/notifications/reports/send-now?report_type=${reportType}`);
export const getReportHistory = (limit = 10) =>
  api.get(`/notifications/reports/history?limit=${limit}`);

// Legacy Report API aliases for backward compatibility
export const setReportPreferences = updateReportPreferences;
export const previewReport = (period = 'weekly') =>
  api.post(`/notifications/reports/send-now?report_type=${period}`);

// Spending Challenges APIs
export const createChallenge = (challenge) =>
  api.post('/challenges/create', challenge);
export const getActiveChallenges = () =>
  api.get('/challenges/active');
export const getAllChallenges = (status = null, limit = 20) =>
  api.get(`/challenges/all?${status ? `status=${status}&` : ''}limit=${limit}`);
export const getChallenge = (challengeId) =>
  api.get(`/challenges/${challengeId}`);
export const cancelChallenge = (challengeId) =>
  api.delete(`/challenges/${challengeId}`);
export const getAllBadges = () =>
  api.get('/challenges/badges/all');
export const getBadgeProgress = () =>
  api.get('/challenges/badges/progress');
export const getChallengeSuggestions = () =>
  api.get('/challenges/suggestions');
export const getChallengeLeaderboard = (limit = 10) =>
  api.get(`/challenges/leaderboard?limit=${limit}`);

// Advanced Budget APIs
export const setCardBudgetSettings = (cardId, settings) =>
  api.post(`/cards/${cardId}/budget-settings`, settings);
export const getCardBudgetSettings = (cardId) =>
  api.get(`/cards/${cardId}/budget-settings`);
export const getCardBudgetStatus = (cardId) =>
  api.get(`/cards/${cardId}/budget-status`);

// AI Recommendations APIs
export const getAIRecommendations = (cardId) =>
  api.get('/analytics/savings-recommendations');
export const applyAIRecommendations = (cardId, recommendations) =>
  api.post('/analytics/savings-recommendations/apply-all', { recommendations });

// 2FA APIs
export const setup2FA = () => api.post('/2fa/setup');
export const verify2FA = (code) => api.post('/2fa/verify', { code });
export const disable2FA = (code) => api.post('/2fa/disable', { code });

// Scheduled/Recurring Transfer APIs
export const createScheduledTransfer = (data) =>
  api.post('/transfers/scheduled/create', data);
export const getScheduledTransfers = () =>
  api.get('/transfers/scheduled');
export const getScheduledTransfer = (transferId) =>
  api.get(`/transfers/scheduled/${transferId}`);
export const pauseScheduledTransfer = (transferId) =>
  api.patch(`/transfers/scheduled/${transferId}/pause`);
export const resumeScheduledTransfer = (transferId) =>
  api.patch(`/transfers/scheduled/${transferId}/resume`);
export const deleteScheduledTransfer = (transferId) =>
  api.delete(`/transfers/scheduled/${transferId}`);
export const executeScheduledTransferNow = (transferId) =>
  api.post(`/transfers/scheduled/${transferId}/execute-now`);
export const reactivateScheduledTransfer = (transferId) =>
  api.patch(`/transfers/scheduled/${transferId}/reactivate`);
export const getSuspendedTransfersCount = () =>
  api.get('/transfers/scheduled/suspended/count');

// Push Notification APIs
export const getVapidPublicKey = () =>
  api.get('/notifications/push/vapid-public-key');
export const subscribePushNotifications = (subscription) =>
  api.post('/notifications/push/subscribe', subscription);
export const unsubscribePushNotifications = (endpoint) =>
  api.delete(`/notifications/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`);

// Password Reset APIs
export const requestPasswordReset = (email) =>
  api.post('/auth/forgot-password', { email });
export const resetPassword = (token, newPassword) =>
  api.post('/auth/reset-password', { token, new_password: newPassword });

// Dashboard Summary
export const getDashboardSummary = () =>
  api.get('/dashboard/summary');

// Spending Insights APIs
export const getSpendingTrends = (months = 6) =>
  api.get(`/insights/spending-trends?months=${months}`);
export const getCategoryBreakdown = (period = 'month') =>
  api.get(`/insights/category-breakdown?period=${period}`);
export const getBudgetVsActual = () =>
  api.get('/insights/budget-vs-actual');
export const getTopMerchants = (limit = 10) =>
  api.get(`/insights/top-merchants?limit=${limit}`);
export const getSpendingByDay = (days = 30) =>
  api.get(`/insights/spending-by-day?days=${days}`);

// Category Management APIs
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/categories', data);
export const deleteCategory = (name) => api.delete(`/categories/${encodeURIComponent(name)}`);
export const updateTransactionCategory = (txId, category) =>
  api.patch(`/transactions/${txId}/category`, { category });
export const bulkCategorize = (transactionIds, category) =>
  api.post('/transactions/bulk-categorize', { transaction_ids: transactionIds, category });
export const autoCategorizeAll = () => api.post('/transactions/auto-categorize');

// Organization/Team APIs
export const createOrganization = (data) => api.post('/organizations', data);
export const getMyOrganization = () => api.get('/organizations/me');
export const inviteTeamMember = (email, role) =>
  api.post('/organizations/invite', { email, role });
export const getInvitations = () => api.get('/organizations/invitations');
export const acceptInvitation = (invitationId) =>
  api.post(`/organizations/invitations/${invitationId}/accept`);
export const updateMemberRole = (memberId, role) =>
  api.patch(`/organizations/members/${memberId}`, { role });
export const removeMember = (memberId) =>
  api.delete(`/organizations/members/${memberId}`);
export const setMemberSpendingLimits = (memberId, limits) =>
  api.post('/organizations/spending-limits', { member_id: memberId, ...limits });
export const getOrgActivity = (limit = 50) =>
  api.get(`/organizations/activity?limit=${limit}`);

// ============ Physical Card APIs (DISABLED - Virtual Cards Only) ============
// Physical card functionality has been removed. Virtual cards are the exclusive offering.
// Keeping exports for backward compatibility - these endpoints are disabled on backend.
export const getCardPricing = () => Promise.resolve({ data: { cards: [], message: 'Physical cards not available' } });
export const orderPhysicalCard = () => Promise.reject(new Error('Physical cards not available'));
export const getPhysicalCardOrders = () => Promise.resolve({ data: { orders: [] } });
export const getPhysicalCardOrder = () => Promise.reject(new Error('Physical cards not available'));
export const activatePhysicalCard = () => Promise.reject(new Error('Physical cards not available'));

// Developer/API Keys
export const createApiKey = (data) => api.post('/developer/api-keys', data);
export const listApiKeys = () => api.get('/developer/api-keys');
export const revokeApiKey = (keyId) => api.delete(`/developer/api-keys/${keyId}`);
export const createWebhook = (data) => api.post('/developer/webhooks', data);
export const listWebhooks = () => api.get('/developer/webhooks');
export const deleteWebhook = (webhookId) => api.delete(`/developer/webhooks/${webhookId}`);
export const getApiUsage = () => api.get('/developer/usage');

// Payment Request / QR Code APIs
export const createPaymentRequest = (data) =>
  api.post('/payment-requests/create', data);
export const listPaymentRequests = (status = null) =>
  api.get(`/payment-requests${status ? `?status=${status}` : ''}`);
export const getPaymentRequest = (requestId) =>
  api.get(`/payment-requests/${requestId}`);
export const fulfillPaymentRequest = (requestId, amount) =>
  api.post(`/payment-requests/${requestId}/pay`, { amount });
export const cancelPaymentRequest = (requestId) =>
  api.delete(`/payment-requests/${requestId}`);

// System Status
export const getSystemStatus = () =>
  api.get('/system/status');

// ============ Google Account Management ============
export const unlinkGoogleAccount = () => api.post('/auth/google/unlink');
export const setPassword = (password) => api.post('/auth/set-password', { password });

// ============ MFA APIs ============
export const getMFAStatus = () => api.get('/mfa/status');
export const setupEmailOTP = (email = null) => api.post('/mfa/email/setup', email ? { email } : {});
export const verifyEmailOTP = (code) => api.post('/mfa/email/verify', { code, method: 'email' });
export const disableEmailOTP = () => api.delete('/mfa/email');
export const setupSMSOTP = (phone_number) => api.post('/mfa/sms/setup', { phone_number });
export const verifySMSOTP = (code) => api.post('/mfa/sms/verify', { code, method: 'sms' });
export const disableSMSOTP = () => api.delete('/mfa/sms');
export const generateBackupCodes = () => api.post('/mfa/backup-codes/generate');
export const verifyBackupCode = (code) => api.post('/mfa/backup-codes/verify', { code });
export const setPrimaryMFAMethod = (method) => api.post(`/mfa/set-primary?method=${method}`);

// ============ Merchant Payment APIs ============
export const createMerchantProfile = (data) => api.post('/merchants/profile', data);
export const getMerchantProfile = () => api.get('/merchants/profile');
export const updateMerchantProfile = (updates) => api.patch('/merchants/profile', updates);
export const regenerateMerchantApiKey = () => api.post('/merchants/profile/regenerate-key');
export const createPaymentButton = (data) => api.post('/merchants/buttons', data);
export const listPaymentButtons = () => api.get('/merchants/buttons');
export const getPaymentButton = (buttonId) => api.get(`/merchants/buttons/${buttonId}`);
export const updatePaymentButton = (buttonId, updates) => api.patch(`/merchants/buttons/${buttonId}`, updates);
export const deletePaymentButton = (buttonId) => api.delete(`/merchants/buttons/${buttonId}`);
export const getMerchantPayments = (limit = 50, status = null) => 
  api.get(`/merchants/payments${status ? `?status=${status}&limit=${limit}` : `?limit=${limit}`}`);
export const getMerchantAnalytics = (days = 30) => api.get(`/merchants/analytics?days=${days}`);

// ============ Card Tracking APIs (DISABLED - Virtual Cards Only) ============
// Physical card tracking functionality has been removed.
export const getTrackableOrders = () => Promise.resolve({ data: [] });
export const getOrderTracking = () => Promise.reject(new Error('Physical cards not available'));
export const getTrackingEvents = () => Promise.reject(new Error('Physical cards not available'));
export const refreshTracking = () => Promise.reject(new Error('Physical cards not available'));
export const getDeliveryNotificationSettings = () => Promise.resolve({ data: {} });
export const updateDeliveryNotificationSettings = () => Promise.resolve({ data: {} });
export const getDeliveryEstimate = () => Promise.resolve({ data: { estimated_days: 0, message: 'Physical cards not available' } });

// ============ Advanced Analytics APIs ============
export const getAnalyticsDashboard = () => api.get('/analytics/dashboard');
export const getSpendingPrediction = (period = 'next_month') => api.get(`/analytics/predictions/spending?period=${period}`);
export const getAnomalyAlerts = (resolved = null) => 
  api.get(`/analytics/anomalies${resolved !== null ? `?resolved=${resolved}` : ''}`);
export const resolveAnomaly = (alertId, resolution) => 
  api.post(`/analytics/anomalies/${alertId}/resolve?resolution=${resolution}`);
export const getSavingsRecommendations = () => api.get('/analytics/savings-recommendations');
export const applyRecommendation = (recId) => api.post(`/analytics/savings-recommendations/${recId}/apply`);
export const getCashFlowForecast = (days = 14) => api.get(`/analytics/cash-flow?days=${days}`);
export const getHealthScore = () => api.get('/analytics/health-score');

// AI Spending Insights APIs
export const getAIForecast = () => api.get('/analytics/ai-forecast');
export const getSpendingComparison = () => api.get('/analytics/spending-comparison');

// ============ Physical Card Orders APIs (DISABLED - Virtual Cards Only) ============
// Physical card functionality has been removed. Virtual cards are the exclusive offering.
export const createCardOrder = () => Promise.reject(new Error('Physical cards not available'));
export const listCardOrders = () => Promise.resolve({ data: { orders: [] } });
export const getCardOrder = () => Promise.reject(new Error('Physical cards not available'));
export const cancelCardOrder = () => Promise.reject(new Error('Physical cards not available'));
export const shipCardOrder = () => Promise.reject(new Error('Physical cards not available'));
export const deliverCardOrder = () => Promise.reject(new Error('Physical cards not available'));

// ============ Merchant Subscription APIs ============
export const getSubscriptionTiers = () => api.get('/merchants/subscriptions/tiers');
export const getCurrentSubscription = () => api.get('/merchants/subscriptions/current');
export const subscribeTo = (tier) => api.post('/merchants/subscriptions/subscribe', { tier });
export const cancelSubscription = () => api.post('/merchants/subscriptions/cancel');

// ============ Merchant Payout APIs ============
export const getPayoutBalance = () => api.get('/merchants/payouts/balance');
export const getPayoutSchedule = () => api.get('/merchants/payouts/schedule');
export const createPayoutSchedule = (scheduleData) => api.post('/merchants/payouts/schedule', scheduleData);
export const deletePayoutSchedule = () => api.delete('/merchants/payouts/schedule');
export const requestManualPayout = (amount, currency = 'USD') => 
  api.post('/merchants/payouts/request', { amount, currency });
export const getPayoutHistory = (limit = 50) => api.get(`/merchants/payouts/history?limit=${limit}`);

// ============ User Limits APIs ============
export const getLimitTiers = () => api.get('/limits/tiers');
export const getMyLimits = () => api.get('/limits/my-limits');
export const requestLimitIncrease = (data) => api.post('/limits/request-increase', data);
export const getLimitRequests = () => api.get('/limits/requests');
export const submitVerification = (data) => api.post('/limits/submit-verification', data);
export const getVerificationStatus = () => api.get('/limits/verification-status');
export const upgradeTier = () => api.post('/limits/upgrade-tier');
export const approveVerification = (submissionId) => api.post(`/limits/admin/approve-verification/${submissionId}`);

// ============ Support Chat APIs (Deborah) ============
export const startChatSession = () => api.post('/support/chat/start');
export const sendChatMessage = (sessionId, content) => api.post(`/support/chat/${sessionId}/message`, { content });
export const getChatMessages = (sessionId) => api.get(`/support/chat/${sessionId}/messages`);
export const escalateChat = (sessionId) => api.post(`/support/chat/${sessionId}/escalate`);
export const resolveChat = (sessionId) => api.post(`/support/chat/${sessionId}/resolve`);
export const getChatHistory = (limit = 10) => api.get(`/support/chat/history?limit=${limit}`);

// ============ Contact Form API ============
export const submitContactForm = (data) => api.post('/contact/submit', data);

// ============ Sessions API ============
export const getSessions = () => api.get('/sessions');
export const revokeSession = (sessionId) => api.delete(`/sessions/${sessionId}`);
export const revokeAllOtherSessions = () => api.delete('/sessions');

// ============ WebSocket Token API ============
export const getWebSocketToken = () => api.get('/auth/ws-token');

// ============ Extended Transfer APIs ============
// Send by phone
export const sendByPhone = (phoneNumber, amount, currency, note) => 
  api.post('/transfer/send-by-phone', { phone_number: phoneNumber, amount, currency, note });

// Send to bank
export const sendToBank = (bankDetails, amount, currency, note) => 
  api.post('/transfer/send-to-bank', { bank_details: bankDetails, amount, currency, note });

// Bank transfers
export const getBankTransfers = () => api.get('/transfer/bank-transfers');
export const getBankTransfer = (transferId) => api.get(`/transfer/bank-transfers/${transferId}`);

// Saved recipients
export const getSavedRecipients = () => api.get('/transfer/recipients');
export const saveRecipient = (data) => api.post('/transfer/recipients', data);
export const deleteRecipient = (recipientId) => api.delete(`/transfer/recipients/${recipientId}`);
export const toggleRecipientFavorite = (recipientId) => api.patch(`/transfer/recipients/${recipientId}/favorite`);

// Phone number management
export const linkPhoneNumber = (phoneNumber) => api.post('/transfer/link-phone', { phone_number: phoneNumber });
export const unlinkPhoneNumber = () => api.delete('/transfer/unlink-phone');
export const getPhoneVerificationStatus = () => api.get('/phone/verification-status');
export const sendPhoneCode = (phoneNumber) => api.post('/phone/send-code', { phone_number: phoneNumber });
export const verifyPhoneCode = (phoneNumber, code) => api.post('/phone/verify-code', { phone_number: phoneNumber, code });

// ============ Extended Merchant APIs ============
// Customers
export const getMerchantCustomers = (sortBy = 'total_spent') => api.get(`/merchants/customers?sort_by=${sortBy}`);
export const getMerchantCustomer = (email) => api.get(`/merchants/customers/${encodeURIComponent(email)}`);
export const addCustomerNote = (email, notes) => api.post(`/merchants/customers/${encodeURIComponent(email)}/notes`, { notes });

// Invoices
export const createInvoice = (data) => api.post('/merchants/invoices', data);
export const getInvoices = (status) => api.get(`/merchants/invoices${status ? `?status=${status}` : ''}`);
export const getInvoice = (id) => api.get(`/merchants/invoices/${id}`);
export const updateInvoice = (id, data) => api.patch(`/merchants/invoices/${id}`, data);
export const sendInvoice = (id) => api.post(`/merchants/invoices/${id}/send`);
export const cancelInvoice = (id) => api.delete(`/merchants/invoices/${id}`);

// Refunds
export const createRefund = (data) => api.post('/merchants/refunds', data);
export const getRefunds = () => api.get('/merchants/refunds');

// Merchant Webhooks
export const createMerchantWebhook = (data) => api.post('/merchants/webhooks', data);
export const getMerchantWebhooks = () => api.get('/merchants/webhooks');
export const deleteMerchantWebhook = (id) => api.delete(`/merchants/webhooks/${id}`);
export const testMerchantWebhook = (id) => api.post(`/merchants/webhooks/${id}/test`);

// ============ White-label API ============
export const applyForPartnership = (data) => api.post('/whitelabel/apply', data);
export const getPartnerProfile = () => api.get('/whitelabel/partner');
export const updatePartnerBranding = (data) => api.patch('/whitelabel/partner/branding', data);
export const regeneratePartnerKeys = () => api.post('/whitelabel/partner/regenerate-keys');
export const generateWidget = (config) => api.post('/whitelabel/widget/generate', config);
export const getPartnerWidgets = () => api.get('/whitelabel/widgets');
export const deleteWidget = (id) => api.delete(`/whitelabel/widgets/${id}`);
export const getPartnerAnalytics = (days = 30) => api.get(`/whitelabel/analytics?days=${days}`);
export const requestPartnerPayout = (data) => api.post('/whitelabel/payouts/request', data);
export const getPartnerPayouts = () => api.get('/whitelabel/payouts');
export const getSDKDocumentation = () => api.get('/whitelabel/docs/sdk');

// Grey Business API - Cross-border payments to Africa
export const getGreyConfig = () => api.get('/grey/config');
export const getGreyCountries = () => api.get('/grey/countries');
export const getGreyBanks = (countryCode) => api.get(`/grey/banks/${countryCode}`);
export const getGreyRate = (data) => api.post('/grey/rates', data);
export const createGreyPayout = (data) => api.post('/grey/payouts', data);
export const getGreyPayoutStatus = (payoutId) => api.get(`/grey/payouts/${payoutId}`);
export const getGreyPayouts = (params) => api.get('/grey/payouts', { params });

// Developer API Keys
export const createDeveloperKey = (data) => api.post('/developer-keys/create', data);
export const listDeveloperKeys = () => api.get('/developer-keys/list');
export const getDeveloperKey = (keyId) => api.get(`/developer-keys/${keyId}`);
export const toggleDeveloperKey = (keyId) => api.patch(`/developer-keys/${keyId}/toggle`);
export const deleteDeveloperKey = (keyId) => api.delete(`/developer-keys/${keyId}`);
export const regenerateDeveloperKey = (keyId) => api.post(`/developer-keys/${keyId}/regenerate`);
export const getDeveloperKeyStats = () => api.get('/developer-keys/usage-stats');

// ============ Forex Rate Alerts APIs ============
export const createRateAlert = (data) => api.post('/exchange/alerts/', data);
export const getRateAlerts = (activeOnly = false) => api.get(`/exchange/alerts/?active_only=${activeOnly}`);
export const getRateAlert = (alertId) => api.get(`/exchange/alerts/${alertId}`);
export const updateRateAlert = (alertId, data) => api.put(`/exchange/alerts/${alertId}`, data);
export const deleteRateAlert = (alertId) => api.delete(`/exchange/alerts/${alertId}`);
export const toggleRateAlert = (alertId) => api.post(`/exchange/alerts/${alertId}/toggle`);
export const getRateAlertStats = () => api.get('/exchange/alerts/summary/stats');

// ============ Forex Historical Rates APIs ============
export const getForexRates = (region) => api.get(`/forex/rates${region ? `?region=${region}` : ''}`);
export const getSingleForexRate = (code) => api.get(`/forex/rates/${code}`);
export const getForexRegions = () => api.get('/forex/regions');
export const refreshForexRates = () => api.post('/forex/refresh');
export const getHistoricalRates = (currencyCode, days = 30) => api.get(`/forex/historical/${currencyCode}?days=${days}`);

// ============ Batch Transfer APIs ============
export const previewBatchTransfer = (data) => api.post('/batch/preview', data);
export const executeBatchTransfer = (data) => api.post('/batch/send', data);
export const parseBatchCSV = (formData) => api.post('/batch/parse-csv', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getBatchHistory = (limit = 20, offset = 0) => api.get(`/batch/history?limit=${limit}&offset=${offset}`);
export const getBatchDetails = (batchId) => api.get(`/batch/${batchId}`);
export const getScheduledBatches = (limit = 20, offset = 0) => api.get(`/batch/scheduled/list?limit=${limit}&offset=${offset}`);
export const cancelScheduledBatch = (batchId) => api.post(`/batch/scheduled/${batchId}/cancel`);
export const executeScheduledBatchNow = (batchId) => api.post(`/batch/scheduled/${batchId}/execute-now`);

// ============ Receipt APIs ============
export const getReceiptUrl = (transactionId, lang = 'en') => `${API_URL}/receipts/${transactionId}?lang=${lang}`;
export const getBatchReceiptUrl = (batchId) => `${API_URL}/receipts/batch/${batchId}`;

// ============ Push Campaign APIs (Admin) ============
export const createCampaign = (data) => api.post('/campaigns/create', data);
export const listCampaigns = (params) => api.get('/campaigns/list', { params });
export const getCampaign = (campaignId) => api.get(`/campaigns/${campaignId}`);
export const updateCampaign = (campaignId, data) => api.patch(`/campaigns/${campaignId}`, data);
export const scheduleCampaign = (campaignId, scheduledAt) => api.post(`/campaigns/${campaignId}/schedule`, null, { params: { scheduled_at: scheduledAt } });
export const sendCampaignNow = (campaignId) => api.post(`/campaigns/${campaignId}/send-now`);
export const cancelCampaign = (campaignId) => api.post(`/campaigns/${campaignId}/cancel`);
export const deleteCampaign = (campaignId) => api.delete(`/campaigns/${campaignId}`);
export const getCampaignAnalytics = () => api.get('/campaigns/analytics/summary');

export default api;
